import BackOfficeBoundariesTab from '../components/Configuration/BackOfficeBoundariesTab';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Space, Typography, message } from 'antd';
import {
  AppstoreOutlined,
  CreditCardOutlined,
  DeploymentUnitOutlined,
  ExperimentOutlined,
  RobotOutlined,
  SettingOutlined,
  SlidersOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../helpers/api/apiConfig';
import { useAuth } from '../context/AuthContext';
import { isMasterUser } from '../helpers/accessControl';
import AreaTypesTab from '../components/Configuration/AreaTypesTab';
import AgentContextsTab from '../components/Configuration/AgentContextsTab';
import ReportSettingsTab from '../components/Configuration/ReportSettingsTab';
import CompanyUsageTab from '../components/Configuration/CompanyUsageTab';
import BackOfficeRangesDefaultsTab from '../components/Configuration/BackOfficeRangesDefaultsTab';
import BackOfficeDevicesTab from '../components/Configuration/BackOfficeDevicesTab';
import ModuleCatalogueTab from '../components/Configuration/ModuleCatalogueTab';
import type { AreaTypeItem } from '../components/Configuration/types';

const { Title, Text } = Typography;

const BACK_OFFICE_TABS = ['subscriptions', 'boundaries', 'devices', 'modules', 'area-types', 'ranges', 'report', 'agents'] as const;
type BackOfficeTab = typeof BACK_OFFICE_TABS[number];

const BackOffice: React.FC = () => {
  const { currentUser: user, logoutUser: logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMaster = isMasterUser(user);
  const currentCompanyId = user?.currentCompanyId || user?.companyId;

  const [areaTypes, setAreaTypes] = useState<AreaTypeItem[]>([]);
  const [areaTypesLoading, setAreaTypesLoading] = useState(false);

  const buildAuthHeaders = useCallback((includeContentType = true) => {
    const token = localStorage.getItem('accessToken');
    const csrfToken = localStorage.getItem('csrfToken');
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(includeContentType ? { 'Content-Type': 'application/json' } : {}),
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    };
  }, []);

  const handleAuthMismatch = useCallback(async (response: Response, errorData: any) => {
    if (response.status === 401 && errorData?.error === 'auth_mismatch') {
      message.error('Session mismatch detected. Please log in again.');
      await logout();
      return true;
    }
    return false;
  }, [logout]);

  const fetchAreaTypes = useCallback(async () => {
    setAreaTypesLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/area-types`, {
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAreaTypes(data?.data || []);
      } else {
        const errorData = await response.json();
        if (await handleAuthMismatch(response, errorData)) return;
        message.error(errorData?.message || 'Failed to load area types');
      }
    } catch (error) {
      console.error('Error fetching area types:', error);
      message.error('Failed to load area types');
    } finally {
      setAreaTypesLoading(false);
    }
  }, [buildAuthHeaders, handleAuthMismatch]);

  useEffect(() => {
    if (!isMaster) return;
    fetchAreaTypes();
  }, [isMaster, fetchAreaTypes]);

  const openCreateAreaType = () => {
    navigate('/back-office/area-types/new');
  };

  const openEditAreaType = (areaType: AreaTypeItem) => {
    navigate(`/back-office/area-types/${areaType.id}/edit`);
  };

  const handleDeleteAreaType = async (areaType: AreaTypeItem) => {
    try {
      const response = await fetch(`${API_BASE_URL}/area-types/${areaType.id}`, {
        method: 'DELETE',
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok || response.status === 204) {
        message.success('Area type deleted');
        fetchAreaTypes();
      } else {
        const errorData = await response.json();
        if (await handleAuthMismatch(response, errorData)) return;
        message.error(errorData?.message || 'Failed to delete area type');
      }
    } catch (error) {
      console.error('Error deleting area type:', error);
      message.error('Failed to delete area type');
    }
  };

  const tabFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab') || 'subscriptions';
    return (BACK_OFFICE_TABS.includes(requestedTab as BackOfficeTab)
      ? requestedTab
      : 'subscriptions') as BackOfficeTab;
  }, [location.search]);

  const sectionTitle = useMemo(() => {
    switch (tabFromQuery) {
      case 'subscriptions':
        return 'Subscriptions';
      case 'boundaries':
        return 'Operating Boundaries';
      case 'devices':
        return 'Devices';
      case 'modules':
        return 'Device Modules and Capabilities';
      case 'area-types':
        return 'Area Types';
      case 'ranges':
        return 'Ranges';
      case 'report':
        return 'Report';
      case 'agents':
        return 'Agents';
      default:
        return 'Back Office';
    }
  }, [tabFromQuery]);

  useEffect(() => {
    const currentTab = new URLSearchParams(location.search).get('tab');
    if (!currentTab || !BACK_OFFICE_TABS.includes(currentTab as BackOfficeTab)) {
      navigate('/back-office?tab=subscriptions', { replace: true });
    }
  }, [location.search, navigate]);

  if (!isMaster) {
    return (
      <Alert
        message="Access Restricted"
        description="Back Office is available only for master users."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div className="configuration-page">
      <div className="configuration-header">
        <Title level={2} style={{ margin: 0 }}>
          <Space>
            <SettingOutlined />
            Back Office
          </Space>
        </Title>
        <Text type="secondary">
          Central management for subscriptions, operating boundaries, devices, area types, range defaults, report settings, and agents.
        </Text>
      </div>

      <div className="configuration-section-title">
        <Space>
          {tabFromQuery === 'subscriptions' && <CreditCardOutlined />}
          {tabFromQuery === 'devices' && <DeploymentUnitOutlined />}
          {tabFromQuery === 'modules' && <ExperimentOutlined />}
          {tabFromQuery === 'area-types' && <AppstoreOutlined />}
          {tabFromQuery === 'ranges' && <SlidersOutlined />}
          {tabFromQuery === 'report' && <SettingOutlined />}
          {tabFromQuery === 'agents' && <RobotOutlined />}
          <Title level={4} style={{ margin: 0 }}>
            {sectionTitle}
          </Title>
        </Space>
      </div>

      {tabFromQuery === 'subscriptions' && (
        <CompanyUsageTab
          buildAuthHeaders={buildAuthHeaders}
          onAuthMismatch={handleAuthMismatch}
        />
      )}

      {tabFromQuery === 'boundaries' && <BackOfficeBoundariesTab buildAuthHeaders={buildAuthHeaders} onAuthMismatch={handleAuthMismatch} />}

      {tabFromQuery === 'devices' && (
        <BackOfficeDevicesTab
          buildAuthHeaders={buildAuthHeaders}
          onAuthMismatch={handleAuthMismatch}
        />
      )}

      {tabFromQuery === 'modules' && (
        <ModuleCatalogueTab
          buildAuthHeaders={buildAuthHeaders}
          onAuthMismatch={handleAuthMismatch}
        />
      )}

      {tabFromQuery === 'area-types' && (
        <AreaTypesTab
          areaTypes={areaTypes}
          areaTypesLoading={areaTypesLoading}
          onOpenCreate={openCreateAreaType}
          onOpenEdit={openEditAreaType}
          onDelete={handleDeleteAreaType}
        />
      )}

      {tabFromQuery === 'report' && <ReportSettingsTab />}

      {tabFromQuery === 'ranges' && (
        <BackOfficeRangesDefaultsTab
          buildAuthHeaders={buildAuthHeaders}
          onAuthMismatch={handleAuthMismatch}
        />
      )}

      {tabFromQuery === 'agents' && (
        <AgentContextsTab
          isMaster={isMaster}
          companyId={currentCompanyId}
          buildAuthHeaders={buildAuthHeaders}
          onAuthMismatch={handleAuthMismatch}
        />
      )}
    </div>
  );
};

export default BackOffice;
